/**
 * Sale Model Payback Panel — consumes the shared Finance Engine
 * (POST /api/finance-engine/compute). No formulas live in this file.
 *
 * When `diesel.total_litres` is 0/empty, the backend auto-applies a
 * 70% BESS utilisation proxy and returns a warning we surface here.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Fuel, TrendingDown, Leaf, Info, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FMT_INR = (v) => v == null ? '—' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const FMT_INT = (v) => v == null ? '—' : Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const FMT_N1  = (v) => v == null ? '—' : Number(v).toFixed(1);
const FMT_N2  = (v) => v == null ? '—' : Number(v).toFixed(2);
const FMT_N4  = (v) => v == null ? '—' : Number(v).toFixed(4);

const DEFAULTS = {
  dg_kwh_per_litre: 3.4,
  diesel_price_inr_per_litre: 96,
  rte: 0.95,
  charging_tariff_inr_per_kwh: 8.30,
  co2_kg_per_litre: 2.68,
  depr_rate: 0.15,
  tax_rate: 0.25,
  installation_exgst_inr: 100000,
};

/**
 * Props:
 *   system:     { energy_kwh, pcs_kw } — optional, used for BESS utilisation proxy
 *   capex:      number — purchase capex ex-GST (falls back to baseCapex prop)
 *   defaultOpen: boolean
 */
export default function SalePaybackPanel({ system = {}, capex = 0, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    diesel_period_months: 12,
    diesel_total_litres: '',
    diesel_total_bill_inr: '',
    capex_exgst_inr: capex || 0,
    installation_exgst_inr: DEFAULTS.installation_exgst_inr,
    // Assumptions
    dg_kwh_per_litre: DEFAULTS.dg_kwh_per_litre,
    diesel_price_inr_per_litre: DEFAULTS.diesel_price_inr_per_litre,
    rte: DEFAULTS.rte,
    charging_tariff_inr_per_kwh: DEFAULTS.charging_tariff_inr_per_kwh,
    co2_kg_per_litre: DEFAULTS.co2_kg_per_litre,
    depr_rate: DEFAULTS.depr_rate,
    tax_rate: DEFAULTS.tax_rate,
  });
  const [out, setOut] = useState(null);
  const [warnings, setWarnings] = useState([]);

  // Keep capex in sync when parent passes a new value
  useEffect(() => {
    setForm(f => (capex && f.capex_exgst_inr !== capex ? { ...f, capex_exgst_inr: capex } : f));
  }, [capex]);

  const buildPayload = useCallback(() => ({
    system: { energy_kwh: system.energy_kwh, pcs_kw: system.pcs_kw },
    purchase: {
      capex_exgst_inr: Number(form.capex_exgst_inr) || 0,
      installation_exgst_inr: Number(form.installation_exgst_inr) || 0,
    },
    diesel: {
      period_months: Number(form.diesel_period_months) || 12,
      total_litres: Number(form.diesel_total_litres) || 0,
      total_bill_inr: form.diesel_total_bill_inr === '' ? null : Number(form.diesel_total_bill_inr),
      diesel_price_inr_per_litre: Number(form.diesel_price_inr_per_litre) || DEFAULTS.diesel_price_inr_per_litre,
    },
    assumptions: {
      dg_kwh_per_litre: Number(form.dg_kwh_per_litre) || DEFAULTS.dg_kwh_per_litre,
      rte: Number(form.rte) || DEFAULTS.rte,
      charging_tariff_inr_per_kwh: Number(form.charging_tariff_inr_per_kwh) || DEFAULTS.charging_tariff_inr_per_kwh,
      co2_kg_per_litre: Number(form.co2_kg_per_litre) || DEFAULTS.co2_kg_per_litre,
      depr_rate: Number(form.depr_rate) || DEFAULTS.depr_rate,
      tax_rate: Number(form.tax_rate) || DEFAULTS.tax_rate,
    },
  }), [form, system]);

  const compute = useCallback(async () => {
    if (!form.capex_exgst_inr) return;
    setBusy(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const { data } = await axios.post(`${API}/finance-engine/compute`, { payload: buildPayload() }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOut(data.derived);
      setWarnings(data.warnings || []);
    } catch (e) {
      console.error('finance-engine compute failed', e);
    } finally {
      setBusy(false);
    }
  }, [form.capex_exgst_inr, buildPayload]);

  // Recompute on any input change when panel is open
  useEffect(() => {
    if (open) compute();
  }, [open, compute]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const input = (label, field, { suffix, step = '1' } = {}) => (
    <div>
      <Label className="text-[10px] text-slate-500 font-medium">{label}</Label>
      <div className="relative mt-0.5">
        <Input type="number" step={step} value={form[field] ?? ''}
          onChange={e => setField(field, e.target.value)}
          className={`h-8 text-xs font-mono ${suffix ? 'pr-10' : ''}`}
          data-testid={`sale-payback-${field}`} />
        {suffix && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">{suffix}</span>}
      </div>
    </div>
  );

  const dieselBillAvailable = out?.diesel?.annualised_bill_inr > 0;
  const netNeg = out?.purchase?.net_savings_inr_per_year <= 0;

  return (
    <div className="bg-white border border-slate-200 rounded" data-testid="sale-payback-panel">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50"
      >
        <div className="flex items-center gap-2">
          <Fuel className="w-4 h-4 text-amber-600" />
          <span className="text-sm font-semibold text-slate-900">Sale Model Payback (Outright Purchase)</span>
          {busy && <span className="text-[10px] text-slate-400">computing…</span>}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {open && (
        <div className="border-t border-slate-100 p-4 space-y-4">
          {/* ── Inputs ── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="col-span-2 lg:col-span-4 text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Diesel Log</div>
            {input('Period (months)', 'diesel_period_months')}
            {input('Total Litres', 'diesel_total_litres', { suffix: 'L' })}
            {input('Total Bill (₹, optional)', 'diesel_total_bill_inr')}
            {input('Diesel Price (fallback)', 'diesel_price_inr_per_litre', { suffix: '₹/L' })}

            <div className="col-span-2 lg:col-span-4 text-[10px] uppercase tracking-wide text-slate-400 font-semibold mt-2">Purchase Capex</div>
            {input('Capex ex-GST', 'capex_exgst_inr', { suffix: '₹' })}
            {input('Installation ex-GST', 'installation_exgst_inr', { suffix: '₹' })}

            <div className="col-span-2 lg:col-span-4 text-[10px] uppercase tracking-wide text-slate-400 font-semibold mt-2">Assumptions</div>
            {input('DG kWh / litre', 'dg_kwh_per_litre', { step: '0.1' })}
            {input('Round-Trip Efficiency', 'rte', { step: '0.01' })}
            {input('Charging Tariff', 'charging_tariff_inr_per_kwh', { suffix: '₹/kWh', step: '0.01' })}
            {input('CO₂ kg / litre', 'co2_kg_per_litre', { step: '0.01' })}
            {input('Depreciation rate', 'depr_rate', { step: '0.01' })}
            {input('Tax rate', 'tax_rate', { step: '0.01' })}
          </div>

          {/* ── Warnings ── */}
          {warnings.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 space-y-1" data-testid="sale-payback-warnings">
              {warnings.map((w, i) => (
                <div key={w || i} className="flex items-start gap-2 text-[11px] text-amber-800">
                  <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" /><span>{w}</span>
                </div>
              ))}
            </div>
          )}

          {/* ── Outputs ── */}
          {out && (
            <div className="space-y-3">
              {/* Effective Payback — single aggregated number (diesel savings + depreciation tax shield) */}
              <div className="grid grid-cols-1 gap-2">
                <Kpi
                  testid="kpi-payback-effective"
                  icon={<TrendingDown className="w-4 h-4" />}
                  label="Effective Payback"
                  value={
                    netNeg ? 'Net savings negative'
                      : out.purchase.payback_years_incl_depr != null
                        ? `${FMT_N1(out.purchase.payback_years_incl_depr)} yrs`
                        : '—'
                  }
                  sub="Diesel savings + depreciation tax shield (aggregated)"
                  tone={netNeg ? 'amber' : 'emerald'}
                />
              </div>

              {/* Working */}
              <div className="bg-slate-50 border border-slate-100 rounded p-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-[11px]">
                <Row label="DG energy (period)" value={`${FMT_INT(out.dg.energy_kwh)} kWh`} mono />
                <Row label="Diesel-only ₹/kWh" value={`₹${FMT_N2(out.dg.cost_inr_per_kwh_diesel_only)}`} mono />
                <Row label="Annualised litres" value={`${FMT_INT(out.diesel.annualised_litres)} L`} mono />
                <Row label="Annualised diesel bill" value={FMT_INR(out.diesel.annualised_bill_inr)} mono />
                <Row label="Annualised DG kWh" value={`${FMT_INT(out.diesel.annualised_dg_kwh)} kWh`} mono />
                <Row label="Annualised charging kWh" value={`${FMT_INT(out.purchase.annualised_charging_kwh)} kWh`} mono />
                <Row label="Annual charging cost" value={FMT_INR(out.purchase.annualised_charging_cost_inr)} mono />
                <Row label="Net operating savings / yr" value={FMT_INR(out.purchase.net_savings_inr_per_year)} mono
                  accent={netNeg ? 'text-red-600' : 'text-emerald-700'} />
                <Row label="Depreciation tax shield / yr" value={FMT_INR(out.purchase.depr_tax_shield_inr_per_year)} mono />
                <Row label="Effective annual benefit" value={FMT_INR(out.purchase.effective_annual_benefit_inr)} mono accent="text-emerald-700" />
                <Row label="Total capex (ex-GST)" value={FMT_INR(out.purchase.total_capex_exgst_inr)} mono />
              </div>

              {/* CO2 */}
              <div className="bg-emerald-50 border border-emerald-100 rounded p-3 flex items-center gap-3">
                <Leaf className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <div className="flex-1 grid grid-cols-3 gap-3 text-[11px]">
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-emerald-800">Annual abatement</p>
                    <p className="font-semibold text-slate-900">{FMT_N1(out.co2.abatement_tco2e_per_year)} tCO₂e</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-emerald-800">5-yr avoided</p>
                    <p className="font-semibold text-slate-900">{FMT_N1(out.co2.abatement_5yr_tco2e)} tCO₂e</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-emerald-800">Emission factor</p>
                    <p className="font-semibold text-slate-900">{FMT_N2(out.co2.emission_factor_kg_per_litre)} kg/L</p>
                  </div>
                </div>
              </div>

              <p className="text-[10px] text-slate-500 flex items-start gap-1.5">
                <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                Illustrative decision-support estimates; actual economics depend on dispatch, tariff, and tax treatment.
              </p>
            </div>
          )}

          <div className="flex justify-end">
            <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={compute} disabled={busy}>
              Recompute
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const Kpi = ({ icon, label, value, sub, tone = 'blue', testid }) => {
  const toneMap = {
    blue: 'bg-blue-50 border-blue-100 text-blue-900',
    emerald: 'bg-emerald-50 border-emerald-100 text-emerald-900',
    amber: 'bg-amber-50 border-amber-100 text-amber-900',
  };
  return (
    <div className={`border rounded p-3 ${toneMap[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide">
        {icon}{label}
      </div>
      <p className="text-lg font-bold mt-1">{value}</p>
      {sub && <p className="text-[10px] opacity-70 mt-0.5">{sub}</p>}
    </div>
  );
};

const Row = ({ label, value, mono, accent }) => (
  <div className="flex items-baseline justify-between border-b border-slate-100 pb-0.5">
    <span className="text-slate-500">{label}</span>
    <span className={`${mono ? 'font-mono' : 'font-medium'} text-slate-900 ${accent || ''}`}>{value}</span>
  </div>
);
